import { useDeferredValue, useEffect, useMemo, useState } from 'react';
import { flushSync } from 'react-dom';
import { Link, useOutletContext, useSearchParams } from 'react-router-dom';
import type { Client, DocumentArtifact, Session } from '@/domain/schema/v1';
import { cleanForOutput, renderWithIdentity } from '@/domain/workflow/documents';
import {
  applyNotation,
  isVerbatimNotation,
  VERBATIM_NOTATIONS,
  type VerbatimNotation,
} from '@/domain/workflow/docModel';
import { getPref, setPref } from '@/platform/prefs';
import { localDate, localDateOf } from '@/domain/workflow/dates';
import { useAutosave } from '../../hooks/useAutosave';
import { useLoad } from '../../hooks/useLoad';
import { useService } from '../../hooks/useService';
import { ConfirmButton, SaveIndicator, SourceBadge } from '../../components/fields';
import { DocView } from '../../components/DocView';
import { CopyButton } from '../../components/CopyButton';
import { ReceiveBox, SendButton } from '../../components/ExternalRoundTrip';
import { repairPrompt } from '@/domain/ai/prompts';
import { DOC_DRAFT_VERSIONS, fieldBody, setFieldBody, withDocRequest } from '@/domain/ai/docDraft';
import { isWholeDocType, missingItems, POLISH_PROMPT, supplementPrompt } from '@/domain/ai/reportWriting';
import { AiBridge } from '@/services/aiBridge';
import { useRequiredSession } from '../../state/session';
import type { ClientOutlet } from './ClientShell';
import { RangeDocMaker, type RangeDocKind } from '../session/DialogueMaker';
import { VerbatimPicker } from '../../components/VerbatimPicker';

const TYPE: Record<DocumentArtifact['documentType'], string> = {
  session_record: '상담기록지',
  case_report: '상담사례 보고서',
  termination_report: '상담 종결 보고서',
  verbatim: '축어록',
  dialogue: '대화록(Verbatim)',
};

const FILE_TYPE: Record<DocumentArtifact['documentType'], string> = {
  session_record: '상담기록지',
  case_report: '사례보고서',
  termination_report: '종결보고서',
  verbatim: '축어록',
  dialogue: '대화록',
};

/**
 * 문서 작업대 (DOCUMENT_OUTPUTS §5, 운영 개편 v1): 위에 새 문서 카드(준비 상태와 함께), 아래에 문서 목록.
 * 누적 자료에서 초안 생성 → 외부 AI로 다듬기 → 칸별 확인 → 승인 → Word.
 */
export function DocumentsScreen() {
  const { client, reloadClient } = useOutletContext<ClientOutlet>();
  const svc = useService();
  const [params, setParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const { data, reload } = useLoad(
    async () => ({
      docs: await svc.listDocuments(client.id),
      sessions: await svc.listSessions(client.id),
      verbatims: (await svc.listVerbatimSelections(client.id)).filter((v) => v.status === 'selected'),
      ready: await svc.caseReportReadiness(client.id),
    }),
    [svc, client.id, client.revision, params.get('doc')],
  );
  const docId = params.get('doc');
  if (!data) return <p className="muted">불러오는 중…</p>;
  const deleted = params.get('deleted');
  if (docId)
    return (
      <DocViewer
        id={docId}
        client={client}
        onBack={() => setParams({})}
        onDeleted={() => setParams({ deleted: docId })}
      />
    );

  const approved = data.sessions.filter((s) => s.status === 'approved');
  const make = (fn: () => Promise<DocumentArtifact>) =>
    void fn()
      .then((d) => setParams({ doc: d.id }))
      .catch((e: Error) => setError(e.message));
  const mm = (ms: number) => `${Math.floor(ms / 60000)}:${String(Math.floor((ms % 60000) / 1000)).padStart(2, '0')}`;

  return (
    <div className="stack">
      {error && (
        <p role="alert" className="warn">
          {error}
        </p>
      )}
      {deleted && (
        <p className="small" role="status">
          초안을 지웠어요.{' '}
          <button
            type="button"
            className="link"
            onClick={() => void svc.restoreDocument(deleted).then(() => setParams({ doc: deleted }))}
          >
            되돌리기
          </button>
        </p>
      )}
      <h2 style={{ margin: '4px 0 0' }}>새 문서</h2>
      <div className="doc-cards">
        <div className="card doc-card">
          <strong className="doc-card-title">상담사례 보고서</strong>
          <span className="muted small">넣을 회기를 골라서 · 외부 AI가 문서 전체를 한 흐름으로 다듬어요</span>
          <ul className="ready-list">
            {data.ready.map((it) => (
              <li key={it.text} className={it.ok ? 'ok' : it.optional ? 'opt' : 'todo'}>
                <span aria-hidden>{it.ok ? '✓' : it.optional ? '·' : '!'}</span>
                <span>
                  {it.path && !it.ok ? <Link to={it.path}>{it.text}</Link> : it.text}
                  {it.note && (
                    <span className="muted small"> · {it.path ? <Link to={it.path}>{it.note}</Link> : it.note}</span>
                  )}
                </span>
              </li>
            ))}
          </ul>
          <details className="verbatim-pre">
            <summary className="small">축어록 구간 미리 고르기 (선택)</summary>
            <p className="muted small">
              고르지 않으면 AI가 찾은 변화 지점 구간이 들어가요. 보고서를 만든 뒤에도 문서 화면에서 바꿀 수 있어요.
            </p>
            <VerbatimPicker clientId={client.id} onChanged={reload} />
          </details>
          <CaseReportMaker
            sessions={data.sessions}
            onMake={(ids) => make(() => svc.createCaseReport(client.id, ids ? { sessionIds: ids } : {}))}
          />
        </div>

        <div className="card doc-card">
          <strong className="doc-card-title">상담 종결 보고서</strong>
          {client.status === 'terminated' ? (
            <>
              <span className="muted small">종결 처리됨 · 전체 회기와 치료 방향으로 써요</span>
              <button
                type="button"
                className="primary"
                onClick={() => make(() => svc.createTerminationReport(client.id))}
              >
                종결보고서 만들기
              </button>
            </>
          ) : (
            <>
              <span className="muted small">
                {client.treatmentPlan?.status === 'completed'
                  ? '치료 완료로 표시했어요. 종결 처리하면 만들 수 있어요.'
                  : '상담을 종결 처리하면 만들 수 있어요.'}
              </span>
              <TerminateForm client={client} onDone={reloadClient} />
            </>
          )}
        </div>

        <div className="card doc-card">
          <strong className="doc-card-title">대화록(수업용) · 축어록</strong>
          <span className="muted small">회기를 고르면 추천 구간이 나오고, 고르면 바로 만들어져요</span>
          <RangeDocPicker sessions={data.sessions} />
          {data.verbatims.length > 0 && (
            <label>
              저장해 둔 구간으로 축어록
              <select
                aria-label="축어록 구간"
                defaultValue=""
                onChange={(e) => e.target.value && make(() => svc.createVerbatimDocument(e.target.value))}
              >
                <option value="">구간 선택</option>
                {data.verbatims.map((v) => {
                  const s = data.sessions.find((x) => x.id === v.sessionId);
                  return (
                    <option key={v.id} value={v.id}>
                      {s?.sessionNumber ?? '?'}회기 {mm(v.startMs)}–{mm(v.endMs)} · {v.processNotes ?? '발췌'}
                    </option>
                  );
                })}
              </select>
            </label>
          )}
        </div>

        <div className="card doc-card">
          <strong className="doc-card-title">상담기록지 (양식 문서)</strong>
          <span className="muted small">승인된 회기의 기록지를 양식 표로 · Word로 저장</span>
          <label>
            회기
            <select
              aria-label="상담기록지 회기"
              defaultValue=""
              onChange={(e) => e.target.value && make(() => svc.createSessionRecord(e.target.value))}
            >
              <option value="">{approved.length ? '회기 선택' : '승인된 회기가 없어요'}</option>
              {approved.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.sessionNumber}회기 ({s.sessionDate})
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      <h2 style={{ margin: '8px 0 0' }}>문서 목록</h2>
      <div className="list-card">
        {data.docs.length === 0 && <p className="muted small">아직 만든 문서가 없어요.</p>}
        {[...data.docs]
          .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
          .map((d) => (
            <button key={d.id} type="button" className="list-item as-button" onClick={() => setParams({ doc: d.id })}>
              <span className="li-main">
                <span className="li-title">{TYPE[d.documentType]}</span>
                <span className="li-sub">
                  {localDateOf(d.asOf)} 기준 · 고친 날 {localDateOf(d.updatedAt)}
                </span>
              </span>
              <span className={d.status === 'approved' ? 'status-pill ok' : 'status-pill'}>
                {d.status === 'approved' ? '승인됨' : '초안'}
              </span>
              <span className="li-go">열기</span>
            </button>
          ))}
      </div>
      <Reflections clientId={client.id} />
    </div>
  );
}

/** 사례보고서에 넣을 회기: 기본은 모두. 회기 번호를 눌러 빼거나, "N회기까지"로 한 번에 */
function CaseReportMaker({ sessions, onMake }: { sessions: Session[]; onMake: (ids: string[] | null) => void }) {
  const list = sessions.filter((x) => !x.deletedAt).sort((a, b) => a.sessionNumber - b.sessionNumber);
  const [off, setOff] = useState<Set<string>>(new Set());
  const on = list.filter((x) => !off.has(x.id));
  const toggle = (id: string) => {
    const n = new Set(off);
    if (n.has(id)) n.delete(id);
    else n.add(id);
    setOff(n);
  };
  const upTo = (num: number) => setOff(new Set(list.filter((x) => x.sessionNumber > num).map((x) => x.id)));
  const label = !list.length
    ? '사례보고서 만들기'
    : !on.length
      ? '회기를 하나 이상 고르세요'
      : on.length === list.length
        ? `모든 회기(${list.length}개)로 사례보고서 만들기`
        : `${on.map((x) => x.sessionNumber).join('·')}회기로 사례보고서 만들기`;
  return (
    <div className="stack" style={{ gap: 6 }}>
      {list.length > 1 && (
        <>
          <span className="small">넣을 회기 (누르면 빼기·넣기)</span>
          <div className="row" style={{ gap: 4, flexWrap: 'wrap' }}>
            {list.map((x) => (
              <button
                key={x.id}
                type="button"
                className={off.has(x.id) ? 'pill-btn' : 'pill-btn on'}
                aria-pressed={!off.has(x.id)}
                aria-label={`${x.sessionNumber}회기 넣기`}
                onClick={() => toggle(x.id)}
              >
                {x.sessionNumber}
              </button>
            ))}
            <select
              aria-label="몇 회기까지"
              value=""
              onChange={(e) =>
                e.target.value === 'all' ? setOff(new Set()) : e.target.value && upTo(Number(e.target.value))
              }
            >
              <option value="">빠르게 고르기…</option>
              <option value="all">모든 회기</option>
              {list.slice(0, -1).map((x) => (
                <option key={x.id} value={x.sessionNumber}>
                  {x.sessionNumber}회기까지
                </option>
              ))}
            </select>
          </div>
        </>
      )}
      <button
        type="button"
        className="primary"
        disabled={list.length > 0 && !on.length}
        onClick={() => onMake(on.length === list.length ? null : on.map((x) => x.id))}
      >
        {label}
      </button>
    </div>
  );
}

function DocViewer({
  id,
  client,
  onBack,
  onDeleted,
}: {
  id: string;
  client: Client;
  onBack: () => void;
  onDeleted: () => void;
}) {
  const svc = useService();
  const { data, reload } = useLoad(
    async () => ({ doc: await svc.openDocument(id), identity: await svc.getIdentity(client.id) }),
    [svc, id],
  );
  if (!data) return <p className="muted">불러오는 중…</p>;
  return (
    <DocEditor
      key={`${data.doc.id}:${data.doc.revision}`}
      doc={data.doc}
      clientCode={client.clientCode}
      projectUrl={client.externalProjectUrl}
      realName={data.identity?.realName}
      onBack={onBack}
      onChanged={reload}
      onDeleted={onDeleted}
    />
  );
}

function DocEditor({
  doc,
  clientCode,
  projectUrl,
  realName,
  onBack,
  onChanged,
  onDeleted,
}: {
  doc: DocumentArtifact;
  clientCode: string;
  projectUrl?: string | undefined;
  realName: string | undefined;
  onBack: () => void;
  onChanged: () => void;
  onDeleted: () => void;
}) {
  const svc = useService();
  const locked = doc.status === 'approved';
  const a = useAutosave<DocumentArtifact>(doc, (patch, rev) => svc.updateDocument(doc.id, patch.content ?? '', rev), {
    readOnly: locked,
  });
  const [showName, setShowName] = useState(false);
  // 사례보고서·종결보고서: 위험 정보가 문서에 빠졌으면 승인 전에 경고 (A30)
  const session = useRequiredSession();
  const { data: mustInclude } = useLoad(
    async () =>
      isWholeDocType(doc.documentType) && doc.status !== 'approved'
        ? (await new AiBridge(session.repo, session.ctx, svc).mustIncludeFor(doc)).filter((m) => m.critical)
        : [],
    [session, svc, doc.id],
  );
  const [editing, setEditing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportMsg, setExportMsg] = useState<string | null>(null);
  const content = a.value!.content;
  const riskMissing = missingItems(content, mustInclude ?? []).map((m) => m.label.replace(/^위험: /, ''));
  // 축어록 표기: 기본은 상1/내1 (국내 상담 수련에서 가장 흔한 방식). 대화록·그 밖의 문서 각각 바꿀 수 있다
  const notationKey = doc.documentType === 'dialogue' ? 'doc.dialogueNotation' : 'doc.verbatimNotation';
  const [notation, setNotation] = useState<VerbatimNotation>(() => {
    const v = getPref(notationKey);
    return isVerbatimNotation(v) ? v : doc.documentType === 'dialogue' ? 'pcsub' : 'ko';
  });
  const rendered = applyNotation(renderWithIdentity(content, realName, showName), notation);
  /** 복사·인쇄·Word·한글: 앱·AI 흔적 없이 */
  const output = cleanForOutput(rendered);
  // 인쇄·PDF할 때만 흔적 지운 본문으로 바꿔 보여준다 (Ctrl+P 포함)
  const [printing, setPrinting] = useState(false);
  useEffect(() => {
    const before = () => flushSync(() => setPrinting(true));
    const after = () => setPrinting(false);
    window.addEventListener('beforeprint', before);
    window.addEventListener('afterprint', after);
    return () => {
      window.removeEventListener('beforeprint', before);
      window.removeEventListener('afterprint', after);
    };
  }, []);
  const hasVerbatim = /^(상|내)\d+/m.test(content);
  const counts = Object.values(doc.fieldSources ?? {}).reduce<Record<string, number>>(
    (m, s) => ({ ...m, [s]: (m[s] ?? 0) + 1 }),
    {},
  );

  /** Word(.docx) 또는 한글(.hwpx): 같은 배치로 기기 안에서 만든다 */
  const exportFile = (kind: 'docx' | 'hwpx') => {
    setExporting(true);
    setExportMsg(null);
    void a
      .flush()
      .then(() => Promise.all([import('../../export/docx'), import('../../export/hwpx')]))
      .then(async ([{ buildDocx, saveFile }, { buildHwpx }]) => {
        const blob =
          kind === 'docx' ? await buildDocx(output, doc.documentType) : await buildHwpx(output, doc.documentType);
        const name = `${clientCode}_${FILE_TYPE[doc.documentType]}_${localDateOf(doc.asOf)}.${kind}`;
        await saveFile(blob, name);
      })
      .catch((e: Error) => setExportMsg(`${kind === 'docx' ? 'Word' : '한글'} 파일을 만들지 못했습니다: ${e.message}`))
      .finally(() => setExporting(false));
  };

  return (
    <div className="doc-layout">
      <div className="doc-main stack">
        <div className="row no-print doc-top">
          <button type="button" onClick={onBack}>
            ← 문서 목록
          </button>
          <span className={locked ? 'status-pill ok' : 'status-pill'}>{locked ? '승인됨' : '초안'}</span>
          <SaveIndicator status={a.status} error={a.error} />
          <span className="grow" />
          <button type="button" onClick={() => setEditing((e) => !e)} disabled={locked}>
            {editing ? '미리보기' : '편집'}
          </button>
          <button type="button" className="soft-btn" disabled={exporting} onClick={() => exportFile('hwpx')}>
            {exporting ? '만드는 중…' : '한글 저장'}
          </button>
          <button type="button" className="soft-btn" disabled={exporting} onClick={() => exportFile('docx')}>
            Word 저장
          </button>
        </div>
        <p className="muted small no-print">
          <SourceBadge source="auto" /> {counts.auto ?? 0} · <SourceBadge source="needs-review" />{' '}
          {counts['needs-review'] ?? 0} · <SourceBadge source="missing" /> {counts.missing ?? 0} — 본문의 [자료 부족],
          [상담자 직접 작성 필요] 표시를 확인하세요. 칸 제목 옆 "고치기"로 그 칸만 고칠 수 있어요.
        </p>
        {exportMsg && (
          <p role="alert" className="warn small no-print">
            {exportMsg}
          </p>
        )}
        {editing && !locked ? (
          <textarea
            aria-label="문서 내용"
            className="doc-edit"
            value={content}
            onChange={(e) => a.set('content', e.target.value)}
          />
        ) : (
          <DocView
            text={printing ? output : rendered}
            type={doc.documentType}
            {...(!locked
              ? {
                  edit: {
                    body: (label: string) => fieldBody(a.value!.content, label) ?? '',
                    save: (label: string, body: string) =>
                      a.set('content', setFieldBody(a.value!.content, label, body)),
                  },
                }
              : {})}
          />
        )}
      </div>

      <aside className="doc-tools stack no-print">
        {!locked && (
          <DocAi
            docId={doc.id}
            wholeDoc={isWholeDocType(doc.documentType)}
            report={doc.documentType === 'case_report' || doc.documentType === 'termination_report'}
            projectUrl={projectUrl}
            startOpen={/\[(상담자 직접 작성 필요|자료 부족|AI 초안 — 확인 필요)/.test(content)}
            beforeImport={() => a.flush()}
            onImported={onChanged}
          />
        )}
        {!locked && doc.documentType === 'case_report' && (
          <div className="card stack">
            <strong>앱이 채우는 칸 새로 고치기</strong>
            <span className="muted small">
              인적 사항·실시한 검사·회기 표·별지를 지금 자료로 다시 채워요 (보고서를 만든 뒤 검사를 넣었을 때 등). 직접
              쓰거나 AI로 쓴 칸은 그대로예요.
            </span>
            <ConfirmButton
              label="새로 고치기"
              confirmText="앱이 채우는 칸만 지금 자료로 바꿀까요? 직접 쓴 칸은 바뀌지 않고, 이전 내용은 이력에 남아요."
              onConfirm={() =>
                void a
                  .flush()
                  .then(() => svc.refreshReportAppFields(doc.id))
                  .then(onChanged)
              }
            />
          </div>
        )}
        {!locked && doc.documentType === 'case_report' && (
          <details className="card">
            <summary>
              <strong>축어록 구간 고르기</strong>{' '}
              <span className="muted small">주요 회기 한 구간 · 핵심 포인트가 많은 순</span>
            </summary>
            <VerbatimPicker clientId={doc.clientId} docId={doc.id} onChanged={onChanged} />
          </details>
        )}
        <div className="card">
          <strong>보기 · 내보내기</strong>
          <div className="row">
            <CopyButton text={output} label="복사" />
            <button type="button" onClick={() => window.print()} title="인쇄 창에서 'PDF로 저장'을 고르면 PDF가 됩니다">
              인쇄 · PDF
            </button>
            {doc.documentType === 'dialogue' && !locked && (
              <button
                type="button"
                title="전사에서 화자를 고친 뒤 누르면 대화 내용만 새로 만듭니다 (다른 칸은 그대로)"
                onClick={() =>
                  void a
                    .flush()
                    .then(() => svc.rebuildDialogueConversation(doc.id))
                    .then(onChanged)
                    .catch((e: Error) => setExportMsg(e.message))
                }
              >
                대화 내용 다시 만들기
              </button>
            )}
          </div>
          {hasVerbatim && (
            <select
              aria-label="축어록 표기"
              value={notation}
              onChange={(e) => {
                const v = e.target.value as VerbatimNotation;
                setNotation(v);
                setPref(notationKey, v);
              }}
            >
              {(Object.keys(VERBATIM_NOTATIONS) as VerbatimNotation[]).map((k) => (
                <option key={k} value={k}>
                  표기: {VERBATIM_NOTATIONS[k].name}
                </option>
              ))}
            </select>
          )}
          {realName && (
            <label className="check small">
              <input type="checkbox" checked={showName} onChange={(e) => setShowName(e.target.checked)} /> 실명 표시
              (켰을 때만 복사·한글·Word·인쇄에 들어가요)
            </label>
          )}
        </div>
        {!locked && riskMissing.length > 0 && (
          <p role="alert" className="warn">
            위험 정보가 문서에 없습니다: {riskMissing.join(', ')}. 승인하기 전에 해당 칸에 넣었는지 확인하세요.
          </p>
        )}
        {!locked ? (
          <div className="doc-approve">
            <ConfirmButton
              className="primary run-next"
              label="문서 승인"
              confirmText={`${riskMissing.length ? `위험 정보(${riskMissing.join(', ')})가 문서에 없습니다. 그래도 승인할까요? ` : ''}승인하면 이 문서는 잠기고, [AI 초안 — 확인 필요] 표시는 지워집니다 (확인한 내용으로 처리).`}
              onConfirm={() =>
                void a
                  .flush()
                  .then(() => svc.approveDocument(doc.id))
                  .then(onChanged)
              }
            />
          </div>
        ) : (
          <p className="muted">승인된 문서입니다 ({doc.approvedAt?.slice(0, 10)}).</p>
        )}
        {!locked && (
          <ConfirmButton
            className="link small"
            label="이 초안 지우기"
            confirmText="이 초안을 지울까요? 문서 목록 위에서 바로 되돌릴 수 있어요."
            onConfirm={() =>
              void a
                .flush()
                .then(() => svc.deleteDocument(doc.id))
                .then(onDeleted)
            }
          />
        )}
      </aside>
    </div>
  );
}

function TerminateForm({ client, onDone }: { client: Client; onDone: () => void }) {
  const svc = useService();
  const [date, setDate] = useState(localDate());
  const [reason, setReason] = useState<NonNullable<Client['terminationReason']>>('plan_completed');
  const [note, setNote] = useState('');
  return (
    <details>
      <summary>상담 종결 처리 (종결보고서용)</summary>
      <div className="stack">
        <label>
          종결일
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </label>
        <label>
          종결 사유
          <select value={reason} onChange={(e) => setReason(e.target.value as typeof reason)}>
            <option value="plan_completed">상담 계획 완료</option>
            <option value="client_request">내담자의 종료 희망</option>
            <option value="referral">타 기관 연계</option>
            <option value="other">기타</option>
          </select>
        </label>
        <input
          aria-label="종결 메모"
          placeholder="메모 (선택)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
        <ConfirmButton
          label="종결 처리"
          confirmText={`${client.clientCode} 상담을 종결로 표시합니다. 기록은 모두 보존됩니다.`}
          onConfirm={() =>
            void svc
              .terminateClient(client.id, {
                terminationDate: date,
                terminationReason: reason,
                ...(note ? { terminationNote: note } : {}),
              })
              .then(onDone)
          }
        />
      </div>
    </details>
  );
}

type NoteKind = 'self-understanding' | 'theological-reflection' | 'supervision';
const NOTE_KINDS: { kind: NoteKind; label: string; where: string }[] = [
  { kind: 'self-understanding', label: '상담자 자신에 대한 이해', where: '사례보고서 IV' },
  { kind: 'supervision', label: '수퍼비전에서 도움 받고 싶은 점', where: '사례보고서 VII' },
  { kind: 'theological-reflection', label: '신학적 성찰', where: '사례보고서 VIII' },
];
type Note = { id: string; kind: NoteKind; text: string; createdAt: string };

/**
 * 사례보고서 IV·VII·VIII의 재료: 상담자가 직접 적는 메모. 적어 둔 것은 종류별로 보이고, 고치거나 지울 수 있다(되돌리기).
 * 보고서를 만들 때 해당 칸에 들어가고, 외부 AI로 쓸 때도 재료로 함께 보낸다.
 */
function Reflections({ clientId }: { clientId: string }) {
  const svc = useService();
  const { data, reload } = useLoad(async () => {
    const r = await svc.listReflections(clientId);
    const notes: Note[] = [
      ...r.reflections.map((x) => ({ id: x.id, kind: x.kind, text: x.text, createdAt: x.createdAt })),
      ...r.supervision.map((x) => ({
        id: x.id,
        kind: 'supervision' as const,
        text: x.supervisionQuestion ?? x.counselorDifficulty ?? '',
        createdAt: x.createdAt,
      })),
    ].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    return notes;
  }, [svc, clientId]);
  const [kind, setKind] = useState<NoteKind>('self-understanding');
  const [text, setText] = useState('');
  const [editing, setEditing] = useState<{ id: string; text: string } | null>(null);
  const [removed, setRemoved] = useState<Note | null>(null);
  const save = () =>
    void (
      kind === 'supervision' ? svc.addSupervisionQuestion(clientId, text) : svc.addReflection(clientId, kind, text)
    ).then(() => {
      setText('');
      reload();
    });
  const notes = data ?? [];
  return (
    <details className="card" open={notes.length > 0 || undefined}>
      <summary>
        <strong>상담자 성찰 · 수퍼비전 질문</strong> <span className="muted small">{notes.length}건</span>
      </summary>
      <p className="muted small">
        한두 줄 메모면 충분해요. 사례보고서를 만들 때 IV·VII·VIII 칸에 들어가고, 외부 AI로 쓸 때 이 메모를 살려 문단으로
        다듬어요.
      </p>
      {removed && (
        <p className="small undo-line" role="status">
          지움: {removed.text.length > 40 ? `${removed.text.slice(0, 40)}…` : removed.text}{' '}
          <button
            type="button"
            className="link"
            onClick={() =>
              void svc.restoreNote(removed.kind, removed.id).then(() => {
                setRemoved(null);
                reload();
              })
            }
          >
            되돌리기
          </button>
        </p>
      )}
      {NOTE_KINDS.map((k) => {
        const list = notes.filter((n) => n.kind === k.kind);
        if (!list.length) return null;
        return (
          <div key={k.kind} className="fm-field">
            <div className="fm-field-head">
              <strong>{k.label}</strong>
              <span className="status-pill">{list.length}개</span>
              <span className="muted small">{k.where}에 들어가요</span>
            </div>
            <ul className="note-list">
              {list.map((n) => (
                <li key={n.id}>
                  {editing?.id === n.id ? (
                    <>
                      <textarea
                        aria-label="메모 고치기"
                        rows={3}
                        value={editing.text}
                        onChange={(e) => setEditing({ id: n.id, text: e.target.value })}
                      />
                      <div className="row">
                        <button
                          type="button"
                          className="primary"
                          disabled={!editing.text.trim()}
                          onClick={() =>
                            void svc.updateNote(n.kind, n.id, editing.text.trim()).then(() => {
                              setEditing(null);
                              reload();
                            })
                          }
                        >
                          저장
                        </button>
                        <button type="button" onClick={() => setEditing(null)}>
                          취소
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <p className="note-text">{n.text}</p>
                      <div className="row note-actions">
                        <span className="muted small">{n.createdAt.slice(0, 10)}</span>
                        <button
                          type="button"
                          className="link small"
                          onClick={() => setEditing({ id: n.id, text: n.text })}
                        >
                          고치기
                        </button>
                        <button
                          type="button"
                          className="link small"
                          onClick={() =>
                            void svc.deleteNote(n.kind, n.id).then(() => {
                              setRemoved(n);
                              reload();
                            })
                          }
                        >
                          지우기
                        </button>
                      </div>
                    </>
                  )}
                </li>
              ))}
            </ul>
          </div>
        );
      })}
      <div className="fm-field stack">
        <strong className="small">새 메모</strong>
        <select aria-label="성찰 종류" value={kind} onChange={(e) => setKind(e.target.value as NoteKind)}>
          {NOTE_KINDS.map((k) => (
            <option key={k.kind} value={k.kind}>
              {k.label}
            </option>
          ))}
        </select>
        <textarea aria-label="성찰 내용" rows={3} value={text} onChange={(e) => setText(e.target.value)} />
        <button type="button" className="primary" disabled={!text.trim()} onClick={save}>
          기록
        </button>
      </div>
    </details>
  );
}

/** 문서 AI 초안 (모든 문서): 빈 칸을 상담 대화·기록 근거로 채운다. 상담자가 쓴 칸은 그대로 */
function DocAi({
  docId,
  wholeDoc,
  report = false,
  projectUrl,
  startOpen,
  beforeImport,
  onImported,
}: {
  docId: string;
  /** 사례보고서·종결보고서: 문서 전체를 한 번에 쓰고, 반영 전에 칸별로 확인 (A30) */
  wholeDoc: boolean;
  /** 보고서(사례·종결): 분량 목표를 함께 보낸다 */
  report?: boolean;
  projectUrl?: string | undefined;
  startOpen: boolean;
  beforeImport: () => Promise<unknown>;
  onImported: () => void;
}) {
  const session = useRequiredSession();
  const svc = useService();
  const [open, setOpen] = useState(startOpen);
  const ai = useMemo(() => new AiBridge(session.repo, session.ctx, svc), [session, svc]);
  // 분량 목표 (보고서): 사례보고서 기본 8~10쪽, 마지막에 쓴 값을 기억
  const [pages, setPages] = useState(() => (report ? (getPref('doc.pageTarget') ?? '8~10쪽') : ''));
  const pagesQ = useDeferredValue(pages);
  const { data, reload } = useLoad(
    async () =>
      open
        ? { packet: await ai.docDraftPacket(docId, { pages: pagesQ }), imports: await ai.docDraftImports(docId) }
        : null,
    [ai, docId, open, pagesQ],
  );
  const failed = data?.imports.filter((x) => x.parseStatus === 'failed').at(-1);
  const last = data?.imports.filter((x) => x.parseStatus === 'parsed').at(-1);
  const [request, setRequest] = useState('');
  const [review, setReview] = useState<{ raw: string; preview: DocPreview } | null>(null);
  const others = last?.parseWarnings ?? [];
  return (
    <details
      className="card no-print ai-card"
      open={open}
      onToggle={(e) => setOpen((e.target as HTMLDetailsElement).open)}
    >
      <summary>
        <strong>AI로 채우기·고치기</strong>{' '}
        <span className="muted small">빈 칸은 채우고, 고칠 칸은 고칩니다 (상담 대화·기록 근거, 외부 AI)</span>
      </summary>
      {!data ? (
        <p className="muted">자료 준비 중…</p>
      ) : (
        <div className="stack">
          {wholeDoc ? (
            <p className="muted small">
              외부 AI가 지도감독 원칙과 칸별 기준에 따라 <strong>문서 전체를 한 흐름으로</strong>, 나열하지 않고
              문단으로 씁니다. 양식(제목·항목)은 바꾸지 않고, 인적사항·회기 표·검사·별지는 앱이 채운 그대로 둡니다.
              사례보고서 축어록은 주고받는 대화로 읽히게 정돈해 옵니다. 답을 가져오면 반영하기 전에 칸별로 바뀐 내용과
              빠진 항목을 보여줍니다. 내담자 코드만 보내며 실명·별칭·연락처는 보내지 않습니다.
            </p>
          ) : (
            <p className="muted small">
              외부 답을 붙여넣으면 바로 반영됩니다: AI가 보낸 칸은 AI가 쓴 대로 채우거나 고치고, 보내지 않은 칸은 그대로
              둡니다 (바뀐 칸에 [AI 초안 — 확인 필요] 표시, 이전 내용은 이력에 남음). 내담자 코드만 보내며
              실명·별칭·연락처는 보내지 않습니다.
            </p>
          )}
          <label className="field">
            <span className="field-label">외부에 요청할 고칠 점 (선택)</span>
            <textarea
              rows={3}
              value={request}
              onChange={(e) => setRequest(e.target.value)}
              placeholder="예: III-② 유발요인을 애착 관점으로 다시 정리, 회기 요약에서 비밀보장 문장 삭제, 수퍼비전 질문을 5개로"
            />
          </label>
          {report && (
            <label className="field">
              <span className="field-label">분량 (A4)</span>
              <input
                aria-label="분량"
                value={pages}
                onChange={(e) => {
                  setPages(e.target.value);
                  setPref('doc.pageTarget', e.target.value);
                }}
                placeholder="예: 8~10쪽 (비우면 제한 없음)"
              />
              <span className="muted small">AI에게 이 분량에 맞춰 칸마다 길이를 조절하라고 함께 보내요.</span>
            </label>
          )}
          <SendButton text={withDocRequest(data.packet, request)} projectUrl={projectUrl} label="① 외부로 보내기" />
          {wholeDoc && (
            <div className="row">
              <CopyButton text={POLISH_PROMPT} label="다듬기 요청 복사" />
              <span className="muted small">
                답이 나열·반복이 많으면 같은 대화에 붙여넣으세요. 다듬은 전체를 다시 줍니다.
              </span>
            </div>
          )}
          {review ? (
            <DocReview
              review={review}
              onCancel={() => setReview(null)}
              onApply={async (only) => {
                await beforeImport();
                const r = await ai.importDocDraft(docId, review.raw, 'claude', { only });
                setReview(null);
                reload();
                if (!r.ok) throw new Error(`읽지 못했습니다 (${r.errors?.join(', ')}). 원문은 저장되었습니다.`);
                onImported();
              }}
            />
          ) : (
            <ReceiveBox
              title="② 외부 답 가져오기"
              hint="답 전체를 복사해 두면 돌아왔을 때 알아서 찾습니다. 같은 대화에서 다시 고쳐 받은 답도 됩니다."
              onSubmit={async (raw) => {
                if (wholeDoc) {
                  const pv = await ai.previewDocDraft(docId, raw);
                  if (pv.ok) {
                    setReview({ raw, preview: pv });
                    return;
                  }
                }
                await beforeImport();
                const r = await ai.importDocDraft(docId, raw);
                reload();
                if (!r.ok) throw new Error(`읽지 못했습니다 (${r.errors?.join(', ')}). 원문은 저장되었습니다.`);
                onImported();
              }}
            />
          )}
          {failed && (!last || failed.createdAt > last.createdAt) && (
            <CopyButton text={repairPrompt(failed.rawOutput, DOC_DRAFT_VERSIONS.output)} label="형식 복구 요청 복사" />
          )}
          {others.length ? (
            <ul className="small">
              {others.map((w, i) => (
                <li key={i}>{w}</li>
              ))}
            </ul>
          ) : null}
        </div>
      )}
    </details>
  );
}

/** 축어록·대화록: 회기를 고르면 추천 구간이 바로 나오고, 하나 고르면 문서가 만들어진다 (구간을 미리 저장할 필요 없음) */
function RangeDocPicker({ sessions }: { sessions: Session[] }) {
  const ready = sessions.filter((s) => s.status !== 'draft' && s.status !== 'processing');
  const [sessionId, setSessionId] = useState('');
  const [kind, setKind] = useState<RangeDocKind>('dialogue');
  const session = ready.find((s) => s.id === sessionId);
  return (
    <div className="stack range-picker">
      <div className="row">
        <select aria-label="축어록·대화록 회기" value={sessionId} onChange={(e) => setSessionId(e.target.value)}>
          <option value="">{ready.length ? '회기 선택' : '처리된 회기가 없습니다'}</option>
          {ready.map((s) => (
            <option key={s.id} value={s.id}>
              {s.sessionNumber}회기 ({s.sessionDate})
            </option>
          ))}
        </select>
        <select aria-label="만들 문서" value={kind} onChange={(e) => setKind(e.target.value as RangeDocKind)}>
          <option value="dialogue">대화록 (수업용, 작성표)</option>
          <option value="verbatim">축어록</option>
        </select>
      </div>
      {session ? (
        <RangeDocMaker key={`${session.id}:${kind}`} session={session} kind={kind} />
      ) : (
        <p className="muted small">회기를 고르면 추천 구간이 나옵니다. 하나 고르면 바로 문서가 만들어집니다.</p>
      )}
    </div>
  );
}

export type DocPreview = Extract<Awaited<ReturnType<AiBridge['previewDocDraft']>>, { ok: true }>;

/** 반영 전 확인: 칸별로 지금 → 새 내용을 보고 받을 칸을 고른다. 빠진 항목은 보충 요청문으로 */
export function DocReview({
  review,
  onApply,
  onCancel,
}: {
  review: { raw: string; preview: DocPreview };
  onApply: (only: string[]) => Promise<void>;
  onCancel: () => void;
}) {
  const { changes, locked, missing } = review.preview;
  const [picked, setPicked] = useState<Set<string>>(() => new Set(changes.map((c) => c.heading)));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const critical = missing.filter((m) => m.critical);
  const toggle = (h: string) =>
    setPicked((cur) => {
      const n = new Set(cur);
      if (n.has(h)) n.delete(h);
      else n.add(h);
      return n;
    });
  return (
    <div className="card stack doc-review">
      <h2>반영 전 확인 — 바뀌는 칸 {changes.length}개</h2>
      {missing.length > 0 && (
        <div className={`card stack ${critical.length ? 'danger-card' : 'warn-card'}`}>
          <strong className="small">
            답에 빠진 항목 {missing.length}개{critical.length ? ' (위험 정보 포함 — 반드시 확인)' : ''}
          </strong>
          <ul className="small">
            {missing.map((m) => (
              <li key={m.label}>{m.label}</li>
            ))}
          </ul>
          <CopyButton text={supplementPrompt(missing)} label="빠진 항목 보충 요청 복사" />
        </div>
      )}
      {locked.length > 0 && <p className="muted small">앱이 채우는 칸이라 반영하지 않음: {locked.join(', ')}</p>}
      {changes.length === 0 && <p className="muted">바뀌는 칸이 없습니다.</p>}
      <ul className="review-list">
        {changes.map((c) => (
          <li key={c.heading} className={picked.has(c.heading) ? 'picked' : undefined}>
            <label className="check">
              <input type="checkbox" checked={picked.has(c.heading)} onChange={() => toggle(c.heading)} />
              <strong>{c.heading}</strong>
            </label>
            <details>
              <summary className="small">내용 비교</summary>
              <div className="review-cols">
                <div>
                  <span className="muted small">지금</span>
                  <p className="review-text muted">{c.before || '(비어 있음)'}</p>
                </div>
                <div>
                  <span className="muted small">새 내용</span>
                  <p className="review-text">{c.after}</p>
                </div>
              </div>
            </details>
          </li>
        ))}
      </ul>
      {err && (
        <p role="alert" className="warn small">
          {err}
        </p>
      )}
      <div className="row">
        <button
          type="button"
          className="primary"
          disabled={busy || picked.size === 0}
          onClick={() => {
            setBusy(true);
            setErr(null);
            void onApply([...picked])
              .catch((e: Error) => setErr(e.message))
              .finally(() => setBusy(false));
          }}
        >
          {busy ? '반영 중…' : `고른 ${picked.size}칸 반영`}
        </button>
        <button type="button" onClick={() => setPicked(new Set(changes.map((c) => c.heading)))}>
          모두 고르기
        </button>
        <button type="button" className="link" onClick={onCancel}>
          취소
        </button>
      </div>
    </div>
  );
}
